import { useEffect, useState } from 'react';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { businessApi } from '@/lib/api-paths';
import { businessRoutes } from '@/lib/route-map';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, Sparkles } from 'lucide-react';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Input, inputBase } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { SegmentedControl } from '@/components/inspector/segmented-control';
import { api } from '@/lib/api';
import type { SurveyAiDraftResponse, SurveyEditResponse } from '@/lib/types';
import { surveyErrorCopy } from './errors';

type Template = 'blank' | 'fleetifier' | 'ai';

const START_FROM_HINT: Record<Template, string> = {
  blank: 'A single question to start from.',
  fleetifier: 'A ready-made consumer survey you can edit.',
  ai: 'Describe your business and what you want to learn — AI drafts the questions.',
};

// Server requires 10..2000 chars; mirror it client-side so the button state
// explains itself instead of a 400.
const MIN_PROMPT_LENGTH = 10;
const MAX_PROMPT_LENGTH = 2000;

// "New survey" — a small modal (title + start-from) that creates a draft and
// jumps straight into the builder. The AI path drafts a full survey from a
// description (1 monthly AI generation) and opens it the same way.
export function NewSurveyButton() {
  // The Business API is addressed by the tenant the canonical route proved.
  // It is never derived from a Location id or a stored preference.
  const businessId = useBusinessId() ?? '';
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [template, setTemplate] = useState<Template>('blank');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Live AI allowance for the hint line (null until loaded; static copy is the
  // fallback — the server is the authority either way).
  const [aiQuota, setAiQuota] = useState<{ used: number; limit: number } | null>(null);

  useEffect(() => {
    if (!open || template !== 'ai' || aiQuota !== null) return;
    let cancelled = false;
    api
      .get<{ aiUsed: number; aiLimit: number }>(businessApi(businessId, 'billing'))
      .then((b) => {
        if (!cancelled) setAiQuota({ used: b.aiUsed, limit: b.aiLimit });
      })
      .catch(() => {
        /* keep the static copy */
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, open, template, aiQuota]);

  const promptTooShort = prompt.trim().length < MIN_PROMPT_LENGTH;
  const quotaCopy =
    aiQuota === null
      ? 'Uses 1 of your monthly AI generations'
      : aiQuota.limit <= 0
        ? 'AI drafting is included on every paid plan'
        : `Uses 1 AI generation — ${Math.max(0, aiQuota.limit - aiQuota.used)} of ${aiQuota.limit} left this month`;

  async function create() {
    setBusy(true);
    setError(null);
    try {
      if (template === 'ai') {
        const res = await api.post<SurveyAiDraftResponse>(businessApi(businessId, 'surveys/ai-draft'), {
          prompt: prompt.trim(),
          ...(title.trim() ? { title: title.trim() } : {}),
        });
        navigate(businessRoutes.surveyEdit(businessId, res.survey.id), {
          state: res.warnings.length > 0 ? { aiWarnings: res.warnings } : undefined,
        });
        return;
      }
      const res = await api.post<SurveyEditResponse>(businessApi(businessId, 'surveys'), {
        title: title.trim() || 'Untitled survey',
        template,
      });
      navigate(businessRoutes.surveyEdit(businessId, res.survey.id));
    } catch (e) {
      setError(e instanceof Error ? surveyErrorCopy(e.message) : 'Could not create the survey.');
      setBusy(false);
    }
  }

  return (
    <>
      <Button type="button" onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        New survey
      </Button>
      <Modal
        open={open}
        onClose={() => !busy && setOpen(false)}
        title="New survey"
        size="md"
        disableClose={busy}
        footer={
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void create()}
              disabled={busy || (template === 'ai' && promptTooShort)}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : template === 'ai' ? (
                <Sparkles className="h-4 w-4" aria-hidden="true" />
              ) : null}
              {template === 'ai' ? (busy ? 'Drafting…' : 'Draft with AI') : 'Create survey'}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          {error ? (
            <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">
              {error}
            </p>
          ) : null}
          <div className="flex flex-col gap-2">
            <Label htmlFor="new-survey-title">Title</Label>
            <Input
              id="new-survey-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder={template === 'ai' ? 'Leave blank to let AI name it' : 'Customer satisfaction'}
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label>Start from</Label>
            <SegmentedControl<Template>
              ariaLabel="Start from"
              value={template}
              onChange={setTemplate}
              options={[
                { value: 'blank', label: 'Blank' },
                { value: 'fleetifier', label: 'Sample survey' },
                { value: 'ai', label: 'AI draft' },
              ]}
            />
            <p className="text-xs text-text-tertiary">{START_FROM_HINT[template]}</p>
          </div>
          {template === 'ai' ? (
            <div className="flex flex-col gap-2">
              <Label htmlFor="new-survey-prompt">Describe your survey</Label>
              <textarea
                id="new-survey-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={4}
                minLength={MIN_PROMPT_LENGTH}
                maxLength={MAX_PROMPT_LENGTH}
                disabled={busy}
                aria-describedby="new-survey-prompt-hint"
                placeholder="e.g. I run a neighborhood coffee cart — I want to learn how often people visit, what they order, and what a fair price for a flat white would be."
                className={cn(inputBase, 'resize-none py-2 leading-relaxed')}
              />
              {/* role=status: the region exists before `busy` flips, so the
                  long-running drafting state is announced (WCAG 4.1.3). */}
              <p id="new-survey-prompt-hint" role="status" className="text-xs text-text-tertiary">
                {busy
                  ? 'Drafting your survey — this usually takes under a minute.'
                  : `At least ${MIN_PROMPT_LENGTH} characters. ${quotaCopy} — you can edit everything afterwards.`}
              </p>
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
